import { memo } from "react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { LazySelect } from "@/components/ui/lazy-select";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, Tag, ShieldCheck } from "lucide-react";
import type { BuildingDataAttributeRow, SubClassRow } from "@/stores/admin/models";
import { ATTRIBUTE_TYPES, VALUE_TYPES, MEASUREMENT_UNITS } from "../../../constants/attributeTypes";
import type { AttributeUpdate } from "./types";
import { MultiSelectDialog } from "@/domains/common/components/molecules/MultiSelectDialog";
import type { Tables } from "@/types/supabase-helpers";
import { GRID_COLUMNS } from "./constants";

interface AttributeTableRowProps {
  attr: BuildingDataAttributeRow;
  attributeUpdate?: AttributeUpdate;
  documentCategories: Tables<"document_categories">[];
  subClasses: SubClassRow[];
  attributeCategoryIds: number[];
  attributeSubClassIds: number[];
  validationCount: number;
  savingAttribute: string | null;
  deletingAttribute: string | null;
  onFieldChange: (
    attributeName: string,
    field: keyof AttributeUpdate,
    value: string | number | boolean | null
  ) => void;
  onGroupChange: (attributeName: string, groupId: number, groupName: string) => void;
  onSave: (attributeName: string) => void;
  onDelete: (attributeName: string) => void;
  onCategoriesChange: (attributeName: string, categoryIds: (number | string)[]) => void;
  onLoadCategories?: (attributeName: string) => void;
  onSubClassesChange: (attributeName: string, subClassIds: (number | string)[]) => void;
  onManageValidation: (attributeName: string) => void;
  style?: React.CSSProperties;
  measureRef?: (node: HTMLDivElement | null) => void;
  dataIndex?: number;
}

const CELL = "flex h-12 items-center px-2";

export const AttributeTableRow = memo(
  ({
    attr,
    attributeUpdate,
    documentCategories,
    subClasses,
    attributeCategoryIds,
    attributeSubClassIds,
    validationCount,
    savingAttribute,
    deletingAttribute,
    onFieldChange,
    onSave,
    onDelete,
    onCategoriesChange,
    onLoadCategories,
    onSubClassesChange,
    onManageValidation,
    style,
    measureRef,
    dataIndex,
  }: AttributeTableRowProps) => {
    const currentValues = attributeUpdate || {
      group_id: attr.group_id,
      group_nm: attr.group_nm,
      name_en: attr.name_en,
      name_et: attr.name_et,
      definition_en: attr.definition_en,
      default_unit: attr.default_unit,
      attribute_type_en: attr.attribute_type_en,
      value_type: attr.value_type,
      display_on_widget: attr.display_on_widget ?? false,
      sort_order: attr.sort_order ?? null,
    };

    return (
      <div
        ref={measureRef}
        role="row"
        data-index={dataIndex}
        style={{ ...style, gridTemplateColumns: GRID_COLUMNS }}
        className="grid border-b transition-colors hover:bg-muted/50"
      >
        <div role="cell" className={`${CELL} text-sm font-medium`}>
          <span className="truncate">{attr.attribute_nm}</span>
        </div>
        <div role="cell" className={CELL}>
          <Input
            value={currentValues.name_en || ""}
            onChange={(e) => onFieldChange(attr.attribute_nm, "name_en", e.target.value)}
            className="h-8 w-full"
          />
        </div>
        <div role="cell" className={CELL}>
          <Input
            value={currentValues.name_et || ""}
            onChange={(e) => onFieldChange(attr.attribute_nm, "name_et", e.target.value)}
            className="h-8 w-full"
          />
        </div>
        <div role="cell" className={CELL}>
          <LazySelect
            value={currentValues.attribute_type_en || ""}
            onValueChange={(value) => onFieldChange(attr.attribute_nm, "attribute_type_en", value)}
            options={ATTRIBUTE_TYPES.map((type) => ({
              value: type.value,
              label: type.label,
            }))}
            placeholder="Select type..."
            className="h-8 w-full"
          />
        </div>
        <div role="cell" className={CELL}>
          <LazySelect
            value={currentValues.value_type || ""}
            onValueChange={(value) => onFieldChange(attr.attribute_nm, "value_type", value)}
            options={VALUE_TYPES.map((type) => ({
              value: type.value,
              label: type.label,
            }))}
            placeholder="Select type..."
            className="h-8 w-full"
          />
        </div>
        <div role="cell" className={CELL}>
          <LazySelect
            value={currentValues.default_unit || ""}
            onValueChange={(value) => onFieldChange(attr.attribute_nm, "default_unit", value)}
            options={MEASUREMENT_UNITS.map((unit) => ({
              value: unit.value,
              label: unit.label,
            }))}
            placeholder="Select unit..."
            className="h-8 w-full"
          />
        </div>
        <div role="cell" className={CELL}>
          <Checkbox
            checked={currentValues.display_on_widget ?? false}
            onCheckedChange={(checked) =>
              onFieldChange(attr.attribute_nm, "display_on_widget", checked === true)
            }
          />
        </div>
        <div role="cell" className={CELL}>
          <Input
            type="number"
            value={currentValues.sort_order ?? ""}
            onChange={(e) =>
              onFieldChange(
                attr.attribute_nm,
                "sort_order",
                e.target.value ? parseInt(e.target.value) || null : null
              )
            }
            className="h-8 w-full"
          />
        </div>
        <div role="cell" className={CELL}>
          <MultiSelectDialog
            compact
            items={documentCategories.map((c) => ({
              id: c.id,
              label: c.category_name,
              description: c.description || undefined,
            }))}
            selectedIds={attributeCategoryIds}
            onChange={(ids) => onCategoriesChange(attr.attribute_nm, ids as number[])}
            onOpenChange={(id) => onLoadCategories?.(id as string)}
            resourceId={attr.attribute_nm}
            placeholder="No categories"
            searchPlaceholder="Search categories..."
            icon={<Tag className="h-3 w-3" />}
          />
        </div>
        <div role="cell" className={CELL}>
          <MultiSelectDialog
            compact
            items={subClasses.map((sc) => ({
              id: sc.id,
              label: sc.sub_class,
              description: sc.sub_class_second || undefined,
            }))}
            selectedIds={attributeSubClassIds}
            onChange={(ids) => onSubClassesChange(attr.attribute_nm, ids)}
            resourceId={attr.attribute_nm}
            placeholder="No sub-classes"
            searchPlaceholder="Search sub-classes..."
            icon={<Tag className="h-3 w-3" />}
          />
        </div>
        <div role="cell" className={CELL}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onManageValidation(attr.attribute_nm)}
            className="h-8 w-full justify-between px-2"
          >
            <div className="flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" />
              <span className="text-xs">{validationCount}</span>
            </div>
          </Button>
        </div>
        <div role="cell" className={CELL}>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSave(attr.attribute_nm)}
              disabled={savingAttribute === attr.attribute_nm}
              className="h-8"
            >
              {savingAttribute === attr.attribute_nm ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => onDelete(attr.attribute_nm)}
              disabled={deletingAttribute === attr.attribute_nm}
              className="h-8"
            >
              {deletingAttribute === attr.attribute_nm ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }
);

AttributeTableRow.displayName = "AttributeTableRow";
